import { z } from 'zod';
import {
  publicEventSchema,
  hostEventSchema,
  voteResultSchema,
  suggestResultSchema,
  withdrawResultSchema,
  whoamiSchema,
  mediaSearchResponseSchema,
  errorSchema,
  type CreateEvent,
  type EditEvent,
  type ManageOptions,
  type SuggestOption,
  type SuggestResult,
  type WithdrawOption,
  type WithdrawResult,
  type ManageInvitees,
  type RegeneratePoster,
  type ApproveArt,
  type CastVote,
  type PublicEvent,
  type HostEvent,
  type VoteResult,
  type WhoAmI,
  type MediaSearchResponse,
} from '../../shared/protocol';

/**
 * Typed client for the Thursday Night worker API. Every response is
 * re-validated with the SAME Zod schemas the worker emits — the client
 * double-checks the server (cheap insurance against deploy skew, same posture
 * as the VR app). In dev, /api is proxied to the local worker by Vite.
 */

// In production set VITE_API_BASE to the deployed worker URL (e.g.
// https://best-friend-night.<account>.workers.dev). Empty = same-origin /api.
const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
  ) {
    super(message);
  }
}

async function call<T>(
  schema: { parse: (x: unknown) => T },
  path: string,
  init?: RequestInit,
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    });
  } catch {
    throw new ApiError('NETWORK', 'Could not reach the server. Is the worker running?', 0);
  }
  const raw: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const e = errorSchema.safeParse(raw);
    throw new ApiError(
      e.success ? e.data.error : 'ERROR',
      e.success ? e.data.message : `Request failed (${res.status}).`,
      res.status,
    );
  }
  return schema.parse(raw);
}

export const api = {
  createEvent: (body: CreateEvent): Promise<HostEvent> =>
    call(hostEventSchema, '/api/events', { method: 'POST', body: JSON.stringify(body) }),

  getPublicEvent: (id: string): Promise<PublicEvent> =>
    call(publicEventSchema, `/api/events/${encodeURIComponent(id)}`),

  getHostEvent: (id: string, token: string): Promise<HostEvent> =>
    call(hostEventSchema, `/api/events/${encodeURIComponent(id)}/host?token=${encodeURIComponent(token)}`),

  editEvent: (id: string, token: string, body: EditEvent): Promise<HostEvent> =>
    call(hostEventSchema, `/api/events/${encodeURIComponent(id)}/edit?token=${encodeURIComponent(token)}`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // host-gated: reconcile the whole ballot (add / edit / remove movies)
  setOptions: (id: string, token: string, body: ManageOptions): Promise<HostEvent> =>
    call(hostEventSchema, `/api/events/${encodeURIComponent(id)}/options?token=${encodeURIComponent(token)}`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // host-gated: add people, remove someone, or set a person's suggestion allowance
  manageInvitees: (id: string, token: string, body: ManageInvitees): Promise<HostEvent> =>
    call(hostEventSchema, `/api/events/${encodeURIComponent(id)}/invitees?token=${encodeURIComponent(token)}`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  whoAmI: (id: string, code: string): Promise<WhoAmI> =>
    call(whoamiSchema, `/api/events/${encodeURIComponent(id)}/whoami?code=${encodeURIComponent(code)}`),

  vote: (id: string, body: CastVote): Promise<VoteResult> =>
    call(voteResultSchema, `/api/events/${encodeURIComponent(id)}/vote`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // voter-code gated: propose a movie. It joins the ballot immediately UNLESS the
  // same film is already on it, in which case nothing is added and this counts as
  // a vote for the existing one — `merged` says which happened (THU-135).
  suggestOption: (id: string, body: SuggestOption): Promise<SuggestResult> =>
    call(suggestResultSchema, `/api/events/${encodeURIComponent(id)}/suggest`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // voter-code gated: take your OWN suggestion back off the ballot. Refused once
  // anyone else has voted for it (HAS_VOTES) — the host removes it from there.
  withdrawOption: (id: string, body: WithdrawOption): Promise<WithdrawResult> =>
    call(withdrawResultSchema, `/api/events/${encodeURIComponent(id)}/withdraw`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // re-bake a parody poster with feedback. Host passes a token (?token=); a suggester
  // omits it and sends their own `code` in the body (server enforces "own pick only").
  regeneratePoster: (id: string, body: RegeneratePoster, token?: string): Promise<PublicEvent> =>
    call(
      publicEventSchema,
      `/api/events/${encodeURIComponent(id)}/regenerate${token ? `?token=${encodeURIComponent(token)}` : ''}`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // approve a baked poster (cosmetic). Same auth shape as regeneratePoster.
  approveArt: (id: string, body: ApproveArt, token?: string): Promise<PublicEvent> =>
    call(
      publicEventSchema,
      `/api/events/${encodeURIComponent(id)}/approve${token ? `?token=${encodeURIComponent(token)}` : ''}`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // host-gated: queue the Poster Review Time bake (the MC's roast audio for the
  // VR theater — the owner's local oven picks it up like a poster job)
  bakeReviewShow: (id: string, token: string): Promise<{ ok: true }> =>
    call(z.object({ ok: z.literal(true) }), `/api/events/${encodeURIComponent(id)}/review?token=${encodeURIComponent(token)}`, {
      method: 'POST',
    }),

  searchMedia: (q: string): Promise<MediaSearchResponse> =>
    call(mediaSearchResponseSchema, `/api/media?q=${encodeURIComponent(q)}`),

  // poster-oven presence (the owner's local baking box — worker/src/oven.ts).
  // online = its heartbeat is fresh; request kicks off the email-the-host flow.
  ovenStatus: (): Promise<OvenStatus> => call(ovenStatusSchema, '/api/oven/status'),

  requestOven: (): Promise<OvenRequestResult> =>
    call(ovenRequestResultSchema, '/api/oven/request', { method: 'POST', body: '{}' }),
};

// ---- poster-oven status (not event-shaped, so the schemas live here) ----
const ovenStatusSchema = z.object({
  online: z.boolean(),
  lastSeenAt: z.string().optional(),
  detail: z.string().optional(),
  request: z
    .object({ state: z.enum(['pending', 'approved']), requestedAt: z.string() })
    .optional(),
});
export type OvenStatus = z.infer<typeof ovenStatusSchema>;

const ovenRequestResultSchema = z.object({
  ok: z.boolean(),
  notified: z.boolean(),
  reason: z.string().optional(),
});
export type OvenRequestResult = z.infer<typeof ovenRequestResultSchema>;
